import { useRef } from "react";
import { useReducedMotion, useScroll, useTransform } from "framer-motion";

const EASE_OUT = [0.22, 0.8, 0.24, 1] as const;

/**
 * Scroll reveal for cards in a grid: each card rises and grows into place,
 * a beat after the one before it. The delay restarts every `perRow` cards
 * so cards further down a long grid don't wait for the whole list.
 */
export function cardReveal(idx: number, perRow = 3) {
  return {
    initial: { opacity: 0, y: 26, scale: 0.94 },
    whileInView: { opacity: 1, y: 0, scale: 1 },
    viewport: { once: true, margin: "0px 0px -60px 0px" },
    transition: { duration: 0.6, delay: (idx % perRow) * 0.09, ease: EASE_OUT },
  };
}

/**
 * Parallax for a section's background layer: returns a ref for the section
 * and a `style` for the background, which drifts by `range` as the section
 * scrolls through the viewport. Off for visitors who prefer reduced motion.
 *
 * `from: "top"` is for a section that starts at the top of the page (the
 * hero): the background starts in place and moves only as it scrolls away.
 */
export function useParallax<T extends HTMLElement>(range: [string, string], from: "top" | "viewport" = "viewport") {
  const ref = useRef<T>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: from === "top" ? ["start start", "end start"] : ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], range);
  return { ref, style: reduce ? undefined : { y } };
}
